import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { noop } from 'ui-components/default-props/noop';

import { emptyArray } from 'ui-components/default-props/empty';

/**
 * @typedef {import('../../types/types').ImportStep} ImportStep
 */

/**
 * @typedef {object} StepDetectiveContextType
 * @property {ImportStep[]} steps
 * @property {ImportStep} activeStep
 * @property {number} activeStepIndex
 * @property {(stepId: string) => string} goTo
 * @property {() => string} goToNext
 * @property {() => string} goToPrevious
 * @property {() => void} markActiveStepAsDone
 */

/** @type {React.Context<StepDetectiveContextType>} */
const StepDetectiveContext = createContext(
  /** @type {StepDetectiveContextType} */ ({
    steps: /** @type {ImportStep[]} */ (emptyArray),
    activeStep: /** @type {ImportStep} */ ({}),
    activeStepIndex: 0,
    goTo: () => '',
    goToNext: () => '',
    goToPrevious: () => '',
    markActiveStepAsDone: noop,
  }),
);

export const useStepDetectiveContext = () => {
  const context = useContext(StepDetectiveContext);
  if (!context) {
    throw new Error('StepDetectiveContext must be used within StepDetectiveProvider');
  }
  return context;
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {ImportStep} [props.startStep]
 * @param {Array<ImportStep>} props.originalSteps
 * @returns {JSX.Element}
 */
export const StepDetectiveProvider = ({ children, startStep, originalSteps }) => {
  const [activeStep, setActiveStep] = useState(/** @type {ImportStep} */ { ...(startStep ?? originalSteps?.[0]) });

  // Ensure activeStep is properly initialized if it starts as empty
  useEffect(() => {
    if (!activeStep?.id && originalSteps?.[0]?.id) {
      setActiveStep({ ...originalSteps[0] });
    }
  }, [activeStep?.id, originalSteps]);

  const steps =
    originalSteps?.map((step) => {
      return { ...(step?.id === activeStep?.id ? activeStep : step) };
    }) ?? emptyArray;

  const activeStepIndex = steps.findIndex((s) => s?.id === activeStep?.id);
  /**
   * @param {string} stepId
   * @returns {string}
   */
  const goTo = (stepId) => {
    const step = steps[steps.findIndex((s) => s?.id === stepId)];
    if (step?.id) {
      setActiveStep(step);
    }

    return step?.id;
  };

  const goToNext = () => {
    const step = steps[activeStepIndex + 1];
    if (step?.id) {
      setActiveStep(step);
    }

    return step?.id;
  };

  const goToPrevious = () => {
    const step = steps[activeStepIndex - 1];
    if (step?.id) {
      setActiveStep(step);
    }

    return step?.id;
  };

  const markActiveStepAsDone = useCallback(() => {
    setActiveStep((prevStep) => ({
      ...prevStep,
      isDone: true,
    }));
  }, []);

  const value = {
    steps,
    activeStep,
    activeStepIndex,
    goTo,
    goToNext,
    goToPrevious,
    markActiveStepAsDone,
  };

  return <StepDetectiveContext.Provider value={value}>{children}</StepDetectiveContext.Provider>;
};
